import { forwardRef, type SelectHTMLAttributes } from 'react';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '../theme/semantic.stylex';

// The house native `<select>`.
//
// Named `NativeSelect` because `Select` is miraidon's Radix combobox, which
// `@sentinel/ui` re-exports and one screen uses. Both belong here: Radix suits
// an unbounded or richly-rendered picker, a real `<select>` suits a bounded
// enum. Two files say so in code — see `invitations/components/status-filter.tsx`
// and `console/features/members/components/role-select.tsx`, which both chose
// native over Radix so Playwright's `selectOption` keeps working.
//
// Before this existed, 26 sites pasted the same eleven declarations with raw
// OKLCH literals in place of the tokens. The styles below reproduce those
// declarations exactly, so adopting this component changes no rendered colour.
//
// Deliberately absent: a `:focus-visible` ring and a `:disabled` rule. Neither
// existed at any of the 26 sites, and adding them would change appearance for
// no gain — a disabled `<select>` already renders at `opacity: 0.7`, and a focus
// ring in `sentinel.borderFocus` would not match the ring miraidon draws on the
// `Input` next to it. Both are filed rather than guessed at.
const styles = stylex.create({
  base: {
    paddingLeft: '0.625rem',
    paddingRight: '0.625rem',
    borderRadius: '0.375rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderEmphasis,
    backgroundColor: sentinel.surfaceElevated,
    color: sentinel.textBody,
    fontSize: '0.875rem',
  },
  md: { paddingTop: '0.5rem', paddingBottom: '0.5rem' },
  sm: { paddingTop: '0.375rem', paddingBottom: '0.375rem' },
});

export interface NativeSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'style' | 'size'> {
  /**
   * Vertical padding. `md` is the dialog and form size, `sm` the compact one
   * used in toolbars and table cells. Named `variantSize` rather than `size`
   * because `<select size>` is a real HTML attribute — the same reason
   * miraidon's `Input` calls it `variantSize`.
   */
  variantSize?: 'md' | 'sm';
  /**
   * Extra StyleX styles, merged after the base so a caller can add layout
   * without replacing it. Takes a `styles` object rather than a `className`
   * on purpose: `stylex.props` output passed as `className` to a component
   * that spreads it after its own would erase the base styling.
   */
  styles?: stylex.StyleXStyles;
  /** Also the default `id`, so a `<Label htmlFor>` needs no extra prop. */
  testId: string;
}

export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(
  function NativeSelect(
    { variantSize = 'md', styles: styleOverride, testId, id, children, ...rest },
    ref,
  ) {
    return (
      <select
        ref={ref}
        id={id ?? testId}
        data-testid={testId}
        {...rest}
        // Spread last on purpose. A component that spreads caller props after
        // its own styling lets a stray `className` erase it — that bug is live
        // on miraidon's table cells today.
        {...stylex.props(styles.base, styles[variantSize], styleOverride)}
      >
        {children}
      </select>
    );
  },
);
